import { useTranslation } from "react-i18next";
import { Archive, Pencil } from "lucide-react";
import type { Product } from "@nh/shared";
import { Button } from "@/shared/ui/core";

export interface ProductRowActionsProps {
  product: Product;
  name: string;
  onEdit: (product: Product) => void;
  onArchive: (product: Product) => void;
  /** `true` mientras se archiva ESTE producto. */
  archiving?: boolean;
}

/**
 * Las acciones de una fila: editar y archivar.
 *
 * No hay "eliminar" y no es un olvido. Borrar un producto de verdad deja
 * cada pedido histórico apuntando a un id que ya no existe, y el
 * historial de compras es exactamente lo que no se puede perder.
 * Archivar lo saca del catálogo y lo deja entero.
 *
 * Los botones llevan el NOMBRE del producto en su `aria-label`: en una
 * tabla de veinticuatro filas, veinticuatro botones que se llaman
 * "Editar" son indistinguibles para quien navega por lista de controles.
 */
export function ProductRowActions({
  product,
  name,
  onEdit,
  onArchive,
  archiving = false,
}: ProductRowActionsProps) {
  const { t } = useTranslation();
  const isArchived = product.status === "archived";

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        leftIcon={<Pencil />}
        aria-label={`${t("Editar")}: ${name}`}
        onClick={() => onEdit(product)}
      />
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        leftIcon={<Archive />}
        loading={archiving}
        disabled={isArchived}
        aria-label={`${t("Archivar")}: ${name}`}
        title={isArchived ? t("Ya está archivado") : t("Archivar")}
        onClick={() => onArchive(product)}
      />
    </div>
  );
}
